// 原系統清單表格的形狀 → 畫面該怎麼排。
//
// 與 legacy-tables.generated.ts 的分工同前兩組：那邊是**資料**（由
// scripts/extract-legacy-tables.ts 從 raw-dom 產生，不手改），這邊是**規則**。
//
// ---------------------------------------------------------------------------
// 解決的問題：整張表差一欄
// ---------------------------------------------------------------------------
// 原系統清單頁的操作連結放在**第一欄**（表頭是空字串，例
// ePortfolio_work/ePortfolio-tsgh/WebContent/WEB-INF/views/config/role.jsp:362），
// React 的 CrudListPage 則一律把「操作」欄接在**最後**。兩邊欄數一樣、位置差一欄，
// 於是每個 th 與每個 td 都對到隔壁去 —— 一頁憑空多出幾十筆差異，而且每一筆看起來
// 都像「文字不同」，完全看不出根因是欄位錯位。
//
// ⚠ 下表是**設計階段的手工 HTML 模擬**（把假的表格 HTML 餵進比對工具估效益用的），
//   不是實作後的實測值。列在這裡是為了留下「哪一步各值多少」的推導軌跡：
//   現況                                    90 筆
//   ＋第一欄補上空表頭的操作欄              77 筆
//   ＋不再於最後追加「操作」欄              67 筆
//   ＋連結欄的儲存格包 <a>                  40 筆
//   **實作後的實測值是 58 筆**（比模擬多 18）：模擬時第一欄留空且只排 9 列，
//   實作版每列第一欄都畫了「刪除」（+9），且 React 是 10 列、連結欄放的是假資料值。
//   要比對效益請看 58；上面四個數字只當推導過程讀。
//
// 這條規則實際套用到 5 頁（config-annual／config-department／config-location／
// config-organization／config-role）—— 原系統有這個毛病的是 38 頁，其餘 33 頁在
// 擷取當下觀察不到欄位形態，被 extract-legacy-tables.ts 的一致性閘門擋在資料外。

import type { Column } from '@/mocks/fixtures';
import { LEGACY_TABLES } from '@/mocks/legacy-tables.generated';

export type { LegacyCellShape, LegacyTableShape } from '@/mocks/legacy-tables.generated';

export interface LegacyTableLayout {
  /** 操作欄要放在第一欄（原系統該欄表頭是空字串）。 */
  readonly actionsFirst: boolean;
  /** 原系統把儲存格包成 `<a>` 的那幾欄（值是 React 的 column.key）。 */
  readonly linkKeys: ReadonlySet<string>;
  /**
   * 原系統用來開「修改」的那一欄（第一個連結欄）。
   * 原系統的修改入口是資料列上的名稱連結（`<a class="showEdit">`，例
   * ePortfolio_work/ePortfolio-tsgh/WebContent/WEB-INF/views/config/role.jsp:71），
   * 不是獨立的「修改」按鈕。有這一欄時，React 的修改動作就掛在它身上。
   */
  readonly editKey: string | null;
}

/**
 * 這一頁的表格該怎麼排；沒有實測形狀、或形狀與現有欄位定義對不上時回傳 undefined
 * （呼叫端維持原本「操作欄接在最後」的行為）。
 *
 * 對不上就整頁不用，是刻意的：形狀資料只要有一頁是錯的，拿它去排版會排出比原本更糟的
 * 結果。下面 :68-69 那兩關（欄數相等、逐格標籤相同）實測 LEGACY_TABLES 的 7 頁全數通過
 * （7/7），但規則還是要寫在這裡，因為 legacy-table-headers（來自 dom.json，且經過
 * Set 去重）與這份形狀資料（來自 raw-dom）是兩個來源，日後有可能漂移。
 */
export function legacyTableLayoutFor(
  pageId: string,
  columns: readonly Column[],
): LegacyTableLayout | undefined {
  const shape = LEGACY_TABLES[pageId];
  if (shape === undefined) return undefined;

  // 原系統表頭去掉空字串之後，必須與現有欄位定義逐格相同，才敢把兩邊的索引對起來。
  const dataIndexes = shape.headers
    .map((header, index) => ({ header, index }))
    .filter((x) => x.header !== '');
  if (dataIndexes.length !== columns.length) return undefined;
  if (dataIndexes.some((x, i) => x.header !== columns[i]?.label)) return undefined;

  const linkKeys = new Set<string>();
  for (const [i, entry] of dataIndexes.entries()) {
    if (shape.shapes[entry.index] === 'link') {
      const key = columns[i]?.key;
      if (key !== undefined) linkKeys.add(key);
    }
  }

  const first = dataIndexes.find((x) => shape.shapes[x.index] === 'link');
  const editKey = first === undefined ? null : (columns[dataIndexes.indexOf(first)]?.key ?? null);

  return { actionsFirst: shape.headers[0] === '', linkKeys, editKey };
}
